import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import ClickableDiv from 'dpl/common/components/ClickableDiv';
import SmartImage from 'dpl/components/SmartImage';
import MediaGridCarousel from 'dpl/components/MediaGrid/MediaGridCarousel';
import MoreImagesCount from 'dpl/testimonials/components/MoreImagesCount';

const COLLAPSED_GALLERY_LENGTH = 3;

function OwnerStoryMediaFile({ image, onClick, imgSize }) {
  return (
    <ClickableDiv className="flex" onClick={onClick}>
      <SmartImage
        className="w-100 br3 object-cover"
        loadingClass="bg-light-gray"
        alt={image.caption}
        src={image.url_small}
        width={imgSize}
        height={imgSize}
        lazy
        crop
      />
    </ClickableDiv>
  );
}

OwnerStoryMediaFile.propTypes = {
  image: PropTypes.shape({
    caption: PropTypes.string,
    url_small: PropTypes.string
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  imgSize: PropTypes.string
};

OwnerStoryMediaFile.defaultProps = {
  imgSize: null
};

export function OwnerStoryGallery({ gallery }) {
  const splideRef = useRef(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(-1);
  const [activeIndex, setActiveIndex] = useState(0);

  const thumbnails = gallery.slice(0, COLLAPSED_GALLERY_LENGTH);

  return (
    <>
      {/* desktop */}
      <div className="dn db-ns mb6">
        <ul className="OwnerStory__gallery grid grid-cols-3 gap-8 relative">
          {thumbnails.map((thumbnail, index) => (
            <li key={thumbnail.id}>
              <OwnerStoryMediaFile
                image={thumbnail}
                onClick={() => setActiveSlideIndex(index)}
              />
            </li>
          ))}
          {gallery.length > COLLAPSED_GALLERY_LENGTH && (
            <div className="absolute right-0 bottom-0 mr2 mb2">
              <MoreImagesCount count={gallery.length} />
            </div>
          )}
        </ul>
      </div>
      {/* mobile web */}
      <div className="db dn-m dn-l mb4">
        <Splide
          ref={splideRef}
          onActive={({ index }) => setActiveIndex(index)}
          options={{
            arrows: false,
            drag: gallery.length > 1,
            dragMinThreshold: 20,
            flickMaxPages: 1,
            flickPower: 300,
            gap: 8,
            keyboard: 'focused',
            pagination: false,
            perPage: 1,
            snap: true,
            start: activeIndex,
            fixedWidth: 268
          }}
        >
          {gallery.map((img, index) => (
            <SplideSlide key={index} className="relative h-100">
              <OwnerStoryMediaFile
                image={img}
                imgSize="268px"
                onClick={() => setActiveSlideIndex(index)}
              />
            </SplideSlide>
          ))}
        </Splide>
      </div>
      {activeSlideIndex >= 0 && (
        <MediaGridCarousel
          mediaFiles={gallery}
          selectedMediaIdx={activeSlideIndex}
          onClose={() => setActiveSlideIndex(-1)}
        />
      )}
    </>
  );
}

OwnerStoryGallery.propTypes = {
  gallery: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string,
      caption: PropTypes.string,
      url: PropTypes.string.isRequired,
      url_small: PropTypes.string.isRequired
    })
  ).isRequired
};
